import { groupBy, keys, sum, values } from 'lodash-es'
import {
    ComboChoiceType,
    ModifierOptionContract,
    OrderLineLookupContract,
    POSVariantGroup,
} from 'models'
import {
    ComboItemViewmodel,
    ComboViewModel,
    convertToItemViewModel,
    EntityModifierViewModel,
    ItemViewModel,
    ModifierOptionLookup,
    POSVariantGroupWithPrice,
    POSVariantOptionWithPrice,
    PriceListData,
    UsedItem,
    UsedItems,
} from 'store/models/sellModels'

import { buildUsedModifiers } from './buildUsedModifiers'
import { buildUsedVariants } from './buildUsedVariants'
import { AddComboPriceToBasePriceListItemData } from 'utilities/price/getPrice'
import Guid from 'utilities/Guid'

export function buildCombo(
    orderLine: OrderLineLookupContract,
    itemsMap: { [key: string]: ItemViewModel },
    modifierOptionsMap: { [key: string]: ModifierOptionContract },
    modifierList: EntityModifierViewModel[],
    comboChoicesMap: { [key: string]: ComboViewModel[] },
    variantGroupsMap: { [variantGroupId: string]: POSVariantGroup },
    priceListData: PriceListData,
): {
    orderItem: ItemViewModel
    usedItems: UsedItems
    oldModifierOptionMaps: { [key: string]: ModifierOptionContract }
    oldModifierList: EntityModifierViewModel[]
    oldComboMap: { [key: string]: ComboViewModel[] }
    oldItems: { [key: string]: ItemViewModel }
    oldVariantGroupsMap: { [variantGroupId: string]: POSVariantGroup }
} {
    const orderItem: ItemViewModel = {
        ...(itemsMap[orderLine.itemID!] ||
            convertToItemViewModel(orderLine.item!!)),
        itemId: orderLine.itemID ?? '',
    }
    const usedItems: { [key: string]: UsedItem } = {}

    let oldModifierOptionMaps: ModifierOptionLookup = {}
    let oldModifierList: EntityModifierViewModel[] = []
    const oldVariantGroupsMap: {
        [variantGroupId: string]: POSVariantGroup
    } = {}
    const oldComboMap: { [itemId: string]: ComboViewModel[] } = {}
    const oldItems: { [itemId: string]: ItemViewModel } = {}
    if (!itemsMap[orderLine.itemID!]) oldItems[orderLine.itemID!] = orderItem
    const subLines = orderLine.subOrderLines
    if (!subLines || subLines.length === 0)
        return {
            orderItem,
            usedItems: {},
            oldModifierOptionMaps,
            oldModifierList,
            oldComboMap,
            oldItems,
            oldVariantGroupsMap,
        }

    /** subLine grouped by SubOrderLine.ComboChoiceID */
    const subLinesGroupByComboChoice = groupBy(subLines, (c) => c.comboChoiceID)

    keys(subLinesGroupByComboChoice).forEach((comboChoiceId) => {
        /** subLine grouped by SubOrderLine.ItemID */
        const subLinesGroupByItem = groupBy(
            subLinesGroupByComboChoice[comboChoiceId],
            (c) => c.itemID,
        )

        /** reference combo choice is the latest combo choice data from API */
        const refComboChoice = comboChoicesMap[orderLine.itemID!]?.find(
            (a) => a.comboChoiceID === comboChoiceId,
        )

        /** all item's id in all sub lines */
        const itemIdsInSubLines = keys(subLinesGroupByItem)
        itemIdsInSubLines.forEach((itemId, itemIndex) => {
            subLinesGroupByItem[itemId].forEach((subLine, idx) => {
                const useMod = buildUsedModifiers(
                    subLine,
                    modifierOptionsMap,
                    modifierList,
                    priceListData,
                )
                const useVar = buildUsedVariants(
                    subLine,
                    itemsMap,
                    variantGroupsMap,
                    true,
                    oldItems,
                )

                let finalItemId = useVar.item.itemId

                oldModifierList = [
                    ...oldModifierList,
                    ...useMod.oldModifierList,
                ]
                oldModifierOptionMaps = {
                    ...oldModifierOptionMaps,
                    ...useMod.oldModifierOptionMaps,
                }
                //create clone usedItem if combo item is not fixed product and has some modifier or variant
                const hasModifier =
                    modifierList.some(
                        (a) => a.entityId === useVar.item.itemId,
                    ) ||
                    modifierList.some(
                        (a) => a.entityId === (useVar.item.baseItemId ?? ''),
                    ) ||
                    modifierList.some(
                        (a) => a.entityId === (useVar.item.categoryId ?? ''),
                    ) ||
                    modifierList.some(
                        (a) =>
                            useVar.item.variantGroups
                                .flatMap(
                                    (g) =>
                                        g.variantOptions?.map(
                                            (a) => a.variantOptionID ?? '',
                                        ) ?? [],
                                )
                                .includes(a.entityId) ?? false,
                    )
                if (
                    refComboChoice?.type !== ComboChoiceType.FixedProduct &&
                    (useVar.item.variantGroups.length > 0 || hasModifier)
                )
                    finalItemId += `_${idx}${itemIndex}`

                const path = `${comboChoiceId},${finalItemId}`

                usedItems[path] = {
                    nodeId: finalItemId,
                    nodeItemId: useVar.item.itemId,
                    isFixedItem:
                        refComboChoice?.type === ComboChoiceType.FixedProduct,
                    quantity: subLine.orderQuantity ?? 0,
                    usedModifiers: useMod.usedModifiers,
                    usedVariants: useVar.usedVariants,
                    comboItemPath: path,
                    comboChoiceId,
                    addPrice: subLine.price ?? 0,
                }
            })
        })

        //validation combo
        const oldComboItem: ComboItemViewmodel[] = []
        if (refComboChoice) {
            // validate on each usage element items (in combo)
            itemIdsInSubLines.forEach((validatingItemId) => {
                /** To use as representative, we picked first subLine from list of subLines which are previously grouped by its item'ID  */
                const firstSubLineInGroup =
                    subLinesGroupByItem[validatingItemId]?.[0]

                /** ItemID that was applied to ComboChoiceItem.ItemID. Generally, this is either or NormalProduct.ItemID (of normal product) or VariantProduct.BaseItem.ItemID */
                const subLineAppliedItemId =
                    firstSubLineInGroup.item?.baseItem?.itemID ??
                    validatingItemId

                const itemUsedInComboChoiceItem = itemsMap[subLineAppliedItemId]
                if (!itemUsedInComboChoiceItem) {
                    oldItems[subLineAppliedItemId] = {
                        ...convertToItemViewModel(
                            firstSubLineInGroup.item?.baseItem ??
                                firstSubLineInGroup.item!,
                        ),
                        isAvailableForSelection: false,
                    }
                }

                /** reference combo choice item that matched with current processing sub line item */
                const refComboChoiceItem = refComboChoice!.comboItems.find(
                    (a) => a.itemID === subLineAppliedItemId,
                )

                /** combo Item price = combo line price - selected variant option price */
                const loadedPrice =
                    (firstSubLineInGroup?.price ?? 0) -
                    sum(
                        firstSubLineInGroup?.orderLineComboVariantOptions?.map(
                            (c) => c.priceAdd ?? 0,
                        ) ?? [],
                    )
                let removeOptions: POSVariantOptionWithPrice[] = []

                const filterDuplicated = (
                    item: POSVariantOptionWithPrice,
                    index: number,
                    items: POSVariantOptionWithPrice[],
                ): boolean =>
                    items.findIndex(
                        (i) => i.variantOptionID === item.variantOptionID,
                    ) === index

                if (refComboChoiceItem) {
                    const selectedVariants =
                        subLinesGroupByItem[validatingItemId]
                            .flatMap(
                                (ig) =>
                                    ig.orderLineComboVariantOptions?.map<
                                        POSVariantOptionWithPrice
                                    >((cvop) => ({
                                        ...cvop.variantOption!,
                                        addPrice: cvop.priceAdd ?? 0,
                                        isDefaultOption: false,
                                    })) ?? [],
                            )
                            .filter(filterDuplicated) ?? []
                    const variantOptions =
                        refComboChoiceItem?.variantGroups?.flatMap(
                            (a) => a.variantOptions,
                        ) ?? []
                    selectedVariants.forEach((selectedOp) => {
                        const varOpt = variantOptions.find(
                            (a) =>
                                a.variantOptionID ===
                                selectedOp.variantOptionID,
                        )
                        if (!varOpt) {
                            removeOptions.push(selectedOp)
                        }
                    })
                }

                if (!(!refComboChoiceItem || removeOptions.length > 0)) {
                    return
                }
                if (!refComboChoiceItem) {
                    removeOptions =
                        subLinesGroupByItem[validatingItemId]
                            ?.flatMap(
                                (a) =>
                                    a.orderLineComboVariantOptions?.map<
                                        POSVariantOptionWithPrice
                                    >((a) => ({
                                        ...a.variantOption!,
                                        addPrice: a.priceAdd ?? 0,
                                        isDefaultOption: false,
                                    })) ?? [],
                            )
                            ?.filter(filterDuplicated) ?? []
                }
                const varGroups: POSVariantGroupWithPrice[] = values(
                    groupBy(removeOptions, (a) => a.variantGroupID),
                ).map((g) => {
                    return {
                        variantGroupID: g[0].variantGroupID,
                        name: 'mock group',
                        variantOptions: g,
                    }
                })
                const mockId = `mockComboItem_${Guid.newGuid()}`
                oldComboItem.push({
                    comboChoiceItemId: mockId,
                    itemID: subLineAppliedItemId,
                    addPrice: loadedPrice,
                    item: convertToItemViewModel(
                        firstSubLineInGroup?.item?.baseItem ??
                            firstSubLineInGroup?.item!,
                    ),
                    variantGroups: varGroups,
                })
                AddComboPriceToBasePriceListItemData(
                    priceListData,
                    mockId,
                    loadedPrice,
                )
            })
        }

        if (!(!refComboChoice || oldComboItem.length > 0)) {
            return
        }
        let choice = refComboChoice
        choice = {
            comboChoiceID: comboChoiceId,
            itemId: orderLine.itemID!,
            quantity: 0,
            name: 'mock Combo choice',
            type: ComboChoiceType.FixedProduct,
            comboItems: oldComboItem,
            createdOn: '0',
        }
        choice.comboItems = oldComboItem
        if (!oldComboMap[orderLine.itemID!]) oldComboMap[orderLine.itemID!] = []
        oldComboMap[orderLine.itemID!].push(choice)
    })
    return {
        orderItem,
        usedItems,
        oldModifierList,
        oldModifierOptionMaps,
        oldComboMap,
        oldVariantGroupsMap,
        oldItems,
    }
}
